/**
 * Canal de ANÁLISE do composer — sobe anexo(s) SEM exigir cliente resolvido
 * (spec-009-anexo-sem-trava, [T008], `contracts/anexo-analise.md` §1).
 *
 * Irmão de `apiFeeFixoCarteiraChat`/`apiTranscricaoUploadTexto`: mesmo
 * racional (REST autenticado próprio, fora da porta única de IA — o LLM não
 * intermedia o upload). A diferença é o motivo de existir: os dois irmãos
 * GRAVAM (carteira no cadastro, transcrição vinculada ao cliente) e por isso
 * exigem cliente; este canal é de LEITURA — a IA lê o arquivo para responder
 * — e o vínculo a um cadastro, se um dia precisar, é passo POSTERIOR (W5,
 * `POST /anexos-analise/{id}/cliente`, fora do escopo desta wave).
 *
 * ★ [T008] O composer sobe um arquivo por chamada
 * (`send-message-anexos-upload-mixin.js::iniciarUploadDoArquivo`), mesmo a
 * rota aceitando 1..N (`arquivos: File[]`, contrato §1). Esta classe sempre
 * manda um lote de 1 e devolve o PRIMEIRO item de `value.anexos` já
 * desembrulhado em `value` — para o mixin ler `r.value.upload_id` do MESMO
 * jeito que já lê dos dois irmãos (nenhuma bifurcação no `.then()` deles).
 */
import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { intelligenceV2 as endpoint } from '@modules/aawz-intelligence-v2/constants/URL'

const MSG_SEM_ARQUIVO = 'Selecione ao menos um arquivo para a IA analisar.'
const MSG_ERRO_UPLOAD =
  'Não foi possível enviar o arquivo para análise. Tente novamente mais tarde ou contate o suporte.'
const MSG_RESPOSTA_SEM_ANEXO =
  'O envio não trouxe o anexo de volta. Tente novamente em instantes; se persistir, contate o suporte.'
const MSG_VINCULO_SEM_DADOS =
  'Não foi possível vincular o anexo: falta o arquivo ou o cliente. Escolha o cliente no chip do composer e tente de novo.'
const MSG_ERRO_VINCULO =
  'Não foi possível vincular o anexo a este cliente. Tente novamente mais tarde ou contate o suporte.'

class ApiAnexosAnalise extends APITemplate {
  /**
   * Sobe UM arquivo para o canal de análise.
   *
   * @param {Object} opts
   * @param {File} opts.arquivo O arquivo a subir.
   * @param {number} [opts.idClienteApp] `app_clientes.id` — OPCIONAL; se
   *   vier, o anexo já nasce vinculado (contrato §1). Ausente/`null`/`0`
   *   nunca é mandado como `id_cliente_app`: sem cliente é o caso comum
   *   deste canal, e mandar `undefined` viraria a string "undefined" no
   *   FormData.
   * @returns {Promise<Object>} `{status, message, value: {upload_id, nome,
   *   content_type, tamanho, expira_em}}`.
   */
  async upload({ arquivo, idClienteApp } = {}) {
    if (!arquivo) {
      return this.buildErrorResponse({
        message: MSG_SEM_ARQUIVO,
        value: null,
        status: 400,
      })
    }

    const dados = { arquivos: [arquivo] }
    if (idClienteApp) dados.id_cliente_app = idClienteApp

    const r = await con.post(endpoint.anexosAnalise, dados)
    if (!r) {
      return this.buildErrorResponse({ message: MSG_ERRO_UPLOAD, value: null })
    }
    if (r.status !== 200) return r

    const anexos = (r.value && r.value.anexos) || []
    if (!anexos.length) {
      return this.buildErrorResponse({
        message: MSG_RESPOSTA_SEM_ANEXO,
        value: null,
      })
    }
    return { status: r.status, message: r.message, value: anexos[0] }
  }

  /**
   * Vincula um anexo JÁ enviado a um cliente (`contracts/anexo-analise.md`
   * §2) — o arquivo não sobe de novo, só ganha dono.
   *
   * É o que fecha a US-3: o consultor anexa sem cliente em foco, a IA lê o
   * arquivo e descobre que precisa de um cadastro, ele escolhe o cliente no
   * chip — e o `upload_id` que já existe no servidor é reaproveitado. Subir
   * o mesmo arquivo outra vez gastaria a rede e criaria um segundo anexo
   * para o mesmo conteúdo.
   *
   * ★ **Idempotente por contrato**: vincular 2× ao MESMO cliente devolve 200
   * nas duas (diferente do canal de carteira, que devolve 409 até para o
   * mesmo cliente — `usecase_fee_fixo_carteira_chat.py:262`). Quem responde
   * 409 aqui é só o anexo que pertence a OUTRO cliente, e a mensagem do
   * servidor NOMEIA o dono: ela vai para a tela como veio, porque é a única
   * que sabe de quem é o arquivo.
   *
   * @param {Object} opts
   * @param {string} opts.uploadId     O `upload_id` opaco devolvido pelo upload.
   * @param {number} opts.idClienteApp `app_clientes.id` do dono.
   * @returns {Promise<Object>} a resposta do servidor (`{status, message, value}`).
   */
  async vincularCliente({ uploadId, idClienteApp } = {}) {
    if (!uploadId || !idClienteApp) {
      return this.buildErrorResponse({
        message: MSG_VINCULO_SEM_DADOS,
        value: null,
        status: 400,
      })
    }

    const r = await con.post(endpoint.anexoAnaliseCliente(uploadId), {
      id_cliente_app: idClienteApp,
    })
    if (!r) {
      return this.buildErrorResponse({ message: MSG_ERRO_VINCULO, value: null })
    }
    return r
  }
}

const apiAnexosAnalise = new ApiAnexosAnalise()
export { apiAnexosAnalise }
export default apiAnexosAnalise
